import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { TrendingUp, TrendingDown, History } from 'lucide-react';

const fmt = (n) => `SR ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Salary change history (raises / cuts) for a partner or employee.
 * Reads GET /salary-changes?entity_type=&entity_id=.
 */
export const SalaryChangesPanel = ({ entityType, entityId, compact = false }) => {
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (entityType) params.set('entity_type', entityType);
    if (entityId) params.set('entity_id', entityId);
    api.get(`/salary-changes?${params.toString()}`)
      .then((r) => { if (active) setChanges(r.data?.changes || []); })
      .catch(() => { if (active) setChanges([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [entityType, entityId]);

  if (loading) return <p className="text-xs text-muted-foreground py-2" data-testid="salary-changes-loading">Loading salary history…</p>;
  if (!changes.length) return <p className="text-xs text-muted-foreground py-2" data-testid="salary-changes-empty">No salary changes recorded yet.</p>;

  return (
    <div className="space-y-1.5" data-testid="salary-changes-panel">
      {changes.map((c) => {
        const up = c.direction === 'increase';
        return (
          <div
            key={c.id}
            className="flex items-center justify-between gap-2 text-xs rounded-md border border-border px-2.5 py-1.5"
            data-testid={`salary-change-${c.id}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${up ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
              </span>
              <span className="tabular-nums">
                {fmt(c.old_salary)} <span className="text-muted-foreground">→</span> <span className="font-semibold">{fmt(c.new_salary)}</span>
              </span>
              <span className={`font-semibold tabular-nums ${up ? 'text-emerald-600' : 'text-rose-600'}`}>
                {up ? '+' : ''}{c.change_pct}%
              </span>
            </div>
            {!compact && (
              <div className="text-[10px] text-muted-foreground text-right shrink-0">
                {String(c.date || '').slice(0, 10)}{c.changed_by_name ? ` · ${c.changed_by_name}` : ''}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SalaryChangesPanel;
